*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --bg:      #0a0a0b;
  --text:    #f2f2ef;
  --muted:   #6f6f78;
  --accent:  #d6ff4f;
  --surface: #17171a;
  --font-display: 'Cormorant Garamond', Georgia, serif;
  --font-mono:    'DM Mono', 'Consolas', monospace;
}

html { scroll-behavior: smooth; }

body {
  background: var(--bg);
  color: var(--text);
  font-family: var(--font-mono);
  font-weight: 300;
  font-size: 14px;
  line-height: 1.7;
  cursor: crosshair;
  -webkit-font-smoothing: antialiased;
}

/* ── Nav ─────────────────────────────────────────── */
nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 3rem;
  background: var(--bg);
  border-bottom: 1px solid transparent;
  transition: border-color 0.3s ease;
}

nav.scrolled { border-bottom-color: var(--muted); }

.nav-logo {
  font-family: var(--font-display);
  font-weight: 300;
  font-size: 1.25rem;
  letter-spacing: 0.05em;
  color: var(--text);
  text-decoration: none;
}

.nav-links {
  display: flex;
  gap: 2.5rem;
  list-style: none;
}

.nav-links a {
  font-family: var(--font-mono);
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text);
  text-decoration: none;
  opacity: 0.45;
  transition: opacity 0.25s ease;
}

.nav-links a:hover { opacity: 1; }

/* ── Tools dropdown ──────────────────────────────── */
.nav-tools {
  position: relative;
}

.nav-tools-btn {
  font-family: var(--font-mono);
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text);
  background: none;
  border: none;
  padding: 0;
  cursor: crosshair;
  opacity: 0.45;
  transition: opacity 0.25s ease;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.nav-tools-btn:hover,
.nav-tools.open .nav-tools-btn { opacity: 1; }

.nav-tools-caret {
  font-size: 0.55rem;
  transition: transform 0.2s ease;
  opacity: 0.6;
}

.nav-tools.open .nav-tools-caret { transform: rotate(180deg); }

.tools-menu {
  position: absolute;
  top: calc(100% + 1.25rem);
  right: 0;
  min-width: 220px;
  background: var(--bg);
  border: 1px solid var(--muted);
  padding: 0.5rem 0;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-6px);
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s;
  z-index: 200;
}

.nav-tools:hover .tools-menu,
.nav-tools.open .tools-menu {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.tools-menu-item {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.75rem 1.1rem;
  text-decoration: none;
  color: var(--text);
  border-left: 2px solid transparent;
  transition: border-color 0.18s ease, background 0.18s ease;
  cursor: crosshair;
}

.tools-menu-item:hover {
  border-left-color: var(--accent);
  background: var(--surface);
}

.tools-menu-item:hover .tools-item-name { color: var(--accent); }

.tools-item-name {
  font-family: var(--font-mono);
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.05em;
  color: var(--text);
  transition: color 0.18s ease;
}

.tools-item-desc {
  font-family: var(--font-mono);
  font-size: 0.65rem;
  font-weight: 300;
  color: var(--muted);
  letter-spacing: 0.02em;
}

/* ── Hero ────────────────────────────────────────── */
#hero {
  min-height: 100vh;
  padding: 0 3rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 4rem;
  padding-top: 6rem;
}

.hero-left {
  animation: fadeUp 0.9s ease forwards;
}

.hero-right {
  animation: fadeUp 0.9s ease 0.22s forwards;
  opacity: 0;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
}

.hero-name {
  font-family: var(--font-display);
  font-weight: 300;
  font-size: clamp(4rem, 9vw, 8rem);
  line-height: 0.95;
  letter-spacing: -0.01em;
  color: var(--text);
  margin-bottom: 1.5rem;
}

.hero-name em {
  font-style: italic;
  color: var(--accent);
}

.hero-subtitle {
  font-family: var(--font-mono);
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--muted);
}

.hero-divider {
  width: 1px;
  height: 3rem;
  background: var(--muted);
  margin: 2rem 0;
}

.interests-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 2.5rem;
}

.interests-list li {
  font-family: var(--font-mono);
  font-size: 0.8rem;
  font-weight: 300;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.interests-list li::before {
  content: '—';
  color: var(--muted);
  flex-shrink: 0;
}

.hero-quote {
  font-family: var(--font-display);
  font-style: italic;
  font-weight: 300;
  font-size: 1.15rem;
  line-height: 1.5;
  color: var(--accent);
  padding-left: 1.25rem;
  border-left: 1px solid var(--muted);
}

/* ── Sections ────────────────────────────────────── */
section {
  padding: 6rem 3rem;
  border-top: 1px solid var(--muted);
}

.section-inner {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 4rem;
  align-items: start;
}

.section-label {
  position: sticky;
  top: 6rem;
}

.section-number {
  font-family: var(--font-display);
  font-size: 3rem;
  font-weight: 300;
  color: var(--surface);
  line-height: 1;
  display: block;
  margin-bottom: 0.25rem;
}

.section-title {
  font-family: var(--font-mono);
  font-size: 0.65rem;
  font-weight: 400;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--muted);
}

/* ── About ───────────────────────────────────────── */
.about-text {
  font-family: var(--font-display);
  font-size: 1.35rem;
  font-weight: 300;
  line-height: 1.65;
  color: var(--text);
  margin-bottom: 2.5rem;
  max-width: 640px;
}

.pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.pill {
  font-family: var(--font-mono);
  font-size: 0.7rem;
  font-weight: 400;
  letter-spacing: 0.04em;
  padding: 0.35rem 0.8rem;
  border: 1px solid var(--muted);
  color: var(--text);
  cursor: crosshair;
  transition: border-color 0.2s ease, color 0.2s ease, background 0.2s ease;
}

.pill:hover {
  border-color: var(--accent);
  color: var(--accent);
  background: color-mix(in srgb, var(--accent) 6%, transparent);
}

/* ── Contact ─────────────────────────────────────── */
.contact-rows {
  display: flex;
  flex-direction: column;
}

.contact-row {
  display: flex;
  align-items: baseline;
  gap: 1.5rem;
  padding: 1.1rem 0;
  border-bottom: 1px solid var(--muted);
  text-decoration: none;
  color: var(--text);
  transition: padding-left 0.25s ease;
  cursor: crosshair;
}

.contact-row:first-child { border-top: 1px solid var(--muted); }

.contact-row:hover { padding-left: 1rem; }

.contact-row:hover .arrow { opacity: 1; }

.platform {
  font-family: var(--font-mono);
  font-size: 0.65rem;
  font-weight: 400;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--muted);
  min-width: 80px;
  flex-shrink: 0;
}

.contact-value {
  font-family: var(--font-mono);
  font-size: 0.85rem;
  font-weight: 300;
  flex: 1;
}

.arrow {
  font-size: 1rem;
  color: var(--accent);
  opacity: 0;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
}

/* ── Footer ──────────────────────────────────────── */
footer {
  padding: 2rem 3rem;
  border-top: 1px solid var(--muted);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

footer span {
  font-family: var(--font-mono);
  font-size: 0.7rem;
  font-weight: 300;
  color: var(--muted);
}

footer em {
  font-style: italic;
  font-family: var(--font-display);
  font-size: 0.85rem;
  color: var(--muted);
}

/* ── Scroll reveal ───────────────────────────────── */
.reveal {
  opacity: 0;
  transform: translateY(32px);
  transition: opacity 0.65s ease, transform 0.65s ease;
}

.reveal.revealed {
  opacity: 1;
  transform: translateY(0);
}

/* ── Responsive ──────────────────────────────────── */
@media (max-width: 768px) {
  nav { padding: 1.25rem 1.5rem; }

  #hero {
    grid-template-columns: 1fr;
    padding: 8rem 1.5rem 4rem;
    min-height: auto;
    gap: 3rem;
  }

  .hero-name { font-size: clamp(3.5rem, 14vw, 5rem); }

  section { padding: 4rem 1.5rem; }

  .section-inner {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .section-label { position: static; }

  footer { padding: 1.5rem; }
}
